import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActionArea,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useAuth } from '../context/AuthContext';
import { getPositionSideLabel } from '../utils/positions';
import FuturesStatusPanel from './FuturesStatusPanel';

// Signed dollar formatter for wheel rows: +$2,903 / -$1,147
const fmtSigned = (v: number | undefined | null, digits: number = 0): string => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: digits })}`;
};

// '2026-08-28' -> '8/28'
const fmtExpiry = (iso?: string): string => {
  if (!iso) return '';
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  return `${parseInt(parts[1], 10)}/${parseInt(parts[2], 10)}`;
};

const wheelStateChipColor = (state: string): 'success' | 'primary' | 'secondary' | 'warning' | 'default' => {
  if (state === 'COVERED') return 'success';
  if (state === 'CSP') return 'primary';
  if (state === 'PCS') return 'secondary';
  if (state === 'SHARES') return 'warning';
  return 'default';
};

const wheelStateTooltip = (state: string): string => {
  if (state === 'CSP') return 'Short put, cash secured';
  if (state === 'COVERED') return 'Assigned shares + covered call working';
  if (state === 'SHARES') return 'Assigned shares, call not yet sold (transitional)';
  if (state === 'PCS') return 'Put credit spread — defined risk (short put + long put)';
  return 'Position outside the strategy state machine';
};

// ── OPT-SPY-DAILY (V1) card helpers ──────────────────────────────────────────

// 775.0 -> "775", 772.5 -> "772.5"
const fmtStrike = (x?: number | null): string =>
  x == null ? '—' : (x % 1 === 0 ? x.toFixed(0) : String(x));

const SPY_EXIT_REASONS: { [k: string]: { label: string; color: 'success' | 'error' | 'warning' | 'default' } } = {
  profit_close: { label: 'TOOK PROFIT', color: 'success' },
  expired_worthless: { label: 'EXPIRED (WIN)', color: 'success' },
  max_loss_close: { label: 'STOPPED OUT', color: 'error' },
  short_strike_breach: { label: 'STRIKE BREACH', color: 'error' },
  flip_breach: { label: 'THESIS BROKE', color: 'error' },
  wall_breach: { label: 'THESIS BROKE', color: 'error' },
  forced_eod_close: { label: 'FORCED FLAT', color: 'default' },
  event_close: { label: 'EVENT CLOSE', color: 'warning' },
  // v0.31.0: operator asked for an immediate exit (flag file) — sign can be either
  operator_close: { label: 'OPERATOR CLOSE', color: 'default' },
  // v0.54.0 LIVE exits — deliberate closes, sign can be either
  giveback_close: { label: 'GIVEBACK', color: 'default' },
  ai_exit: { label: 'AI EXIT', color: 'default' },
  // v0.56.0 LIVE: fires only after 35% captured — a win by design
  profit_floor_close: { label: 'PROFIT FLOOR', color: 'success' },
  // v0.69.0 LIVE: the AI chose the exit after the floor fired
  floor_ai_exit: { label: 'FLOOR AI', color: 'default' },
};

// "16.5" not "17" — IV reject reasons log three decimals since v0.15.1
const fmtIvPct = (x: string): string => `${parseFloat((parseFloat(x) * 100).toFixed(1))}%`;

// A no-trade day is a correct outcome for this strategy — explain it calmly.
const prettySpyStandAside = (reason: string): string => {
  if (!reason) return 'standing aside';
  if (reason.startsWith('negative_regime_iv_too_high')) {
    const m = reason.match(/([\d.]+)>([\d.]+)/);
    return m
      ? `dealers short gamma, IV too high (${fmtIvPct(m[1])} > ${fmtIvPct(m[2])} gate)`
      : 'dealers short gamma, IV too high';
  }
  if (reason.startsWith('atm_iv_veto')) {
    const m = reason.match(/([\d.]+)>([\d.]+)/);
    return m
      ? `panic-priced IV (${fmtIvPct(m[1])} > ${fmtIvPct(m[2])} veto) — no structure trusted in any regime`
      : 'panic-priced IV — no structure trusted in any regime';
  }
  if (reason.startsWith('negative_regime')) return 'dealers short gamma — standing aside';
  if (reason.includes('credit_too_thin')) {
    // v0.27.0 appends the priced strikes (…_771/773); v0.28.0 may append an
    // |at_wall_… suffix (the wall strike itself was also tried and refused) —
    // keep the card line compact, the full story lives on the GEX tab
    const [main, atWall] = reason.split('|');
    const m = main.match(/([\d.]+)<([\d.]+)/);
    const strikes = main.match(/_(\d+(?:\.\d+)?\/\d+(?:\.\d+)?)$/);
    let text = m
      ? `spread${strikes ? ` (${strikes[1]})` : ''} didn't pay enough (${Math.round(parseFloat(m[1]) * 100)}¢ < ${Math.round(parseFloat(m[2]) * 100)}¢ floor)`
      : "spread didn't pay enough";
    if (atWall) text += ' — wall-strike fallback also refused';
    return text;
  }
  // v0.25.1: the protective long leg had no bid — a no-premium morning
  if (reason.startsWith('long_leg_no_quote') || reason === 'no_long_leg_at_width') {
    return 'protective leg had no bid — no spread to sell';
  }
  if (reason.startsWith('long_leg_quote_too_wide')) {
    return 'protective leg quoted too wide to trust';
  }
  // v0.68.0 down-day call gate
  if (reason.includes('down_day_call_block')) {
    const m = reason.match(/down_day_call_block_([+-]?[\d.]+)/);
    return `SPY ${m ? `${m[1].startsWith('-') ? '' : '−'}${m[1].replace('-', '−')}%` : 'down ≥ 1%'} — no call spreads after a selloff`;
  }
  // v0.54.0: wall-stability entry gate — stabilized wall read failed the
  // cushion floor (keep the card line compact; details on the GEX tab)
  if (reason.startsWith('wall_unstable_mode5')) {
    const m = reason.match(/wall_unstable_mode5_(put|call)_([\d.]+)_vs_raw_([\d.]+)/);
    return m
      ? `${m[1]} wall unstable (mode5 ${m[2]} vs raw ${m[3]}) — entry skipped`
      : 'wall unstable — entry skipped';
  }
  // v0.36.0: the dealer map's regime label was untrustworthy — no trade
  // (flips=0 = net too weak; flips>0 = sign flipped through the open)
  if (reason.startsWith('map_undecided')) {
    const m = reason.match(/flips=(\d+)_net=([+-][\d.]+M)/);
    if (m && m[1] === '0') return `map too weak to trust (net ${m[2]}) — no regime call`;
    if (m) return `map flipped sign ${m[1]}× through the open — no regime, no trade`;
    return 'map too weak or unstable to trust — no regime call';
  }
  // v0.17.1: cushion rejects may carry a `call:` prefix (call side needs a
  // wider bar; a call-side reject followed by a put entry is a deliberate
  // reroute to the safer side, not an error) and show the applied threshold.
  const isCallCushion = reason.startsWith('call:');
  const cushion = isCallCushion ? reason.slice('call:'.length) : reason;
  if (cushion.startsWith('exit_cushion_too_thin')) {
    const m = cushion.match(/([\d.]+)%<([\d.]+)%/);
    const nums = m ? ` (${m[1]}% < ${m[2]}%${isCallCushion ? ' call-side bar' : ''})` : '';
    return `exit line too close to price${nums} — normal noise would stop us out immediately${isCallCushion ? '; the system rechecks the put side' : ''}`;
  }
  if (reason.startsWith('event_veto')) return `event veto — ${reason.split(':')[1] || 'scheduled event'}`;
  // v0.19.0: entries deliberately blocked through a morning-session event
  if (reason.startsWith('event_blackout_until_')) {
    const hhmm = reason.slice('event_blackout_until_'.length);
    return `standing aside through the scheduled event — scans continue, entries resume ${hhmm} ET`;
  }
  return reason.replace(/_/g, ' ');
};

// v0.15.x entry-failure reasons (OPEN_FAILED rows: the system wanted the
// trade but wouldn't chase below the credit floor)
const prettySpyOpenFailed = (reason?: string | null): string => {
  if (!reason) return "market wouldn't fill at a worthwhile price";
  if (reason === 'chase_floor_reached') return 'credit floor held — the system refused to chase lower';
  if (reason.startsWith('unfilled_after')) return "orders wouldn't fill at a price worth taking";
  return reason.replace(/_/g, ' ');
};

// calm ≤15%, elevated 15–25%, high >25%
const spyIvColor = (iv: number): 'success' | 'warning' | 'error' =>
  iv <= 0.15 ? 'success' : iv <= 0.25 ? 'warning' : 'error';

// v0.16.1: short label for the economic-calendar badge ("CPI day")
const spyShortEvent = (e: string): string => {
  const s = e.toLowerCase();
  if (s.includes('fomc')) return 'FOMC';
  if (s.includes('consumer price')) return 'CPI';
  if (s.includes('nonfarm') || s.includes('non-farm')) return 'NFP';
  if (s.includes('producer price')) return 'PPI';
  if (s.includes('pce') || s.includes('personal consumption')) return 'PCE';
  return e;
};

// Instances whose card uses the grouped wheel/spread view
const WHEEL_CARD_INSTANCES = ['GROK', 'OPTIONS_PCS', 'E_OPTIONS'];
// Wheel-card instances that also keep the per-sub-account Today's P&L block
// (their income split renders compactly inside the positions section instead)
const WHEEL_CARD_KEEP_PNL = ['E_OPTIONS'];

const instances = [
  { name: 'LIVE', type: 'LIVE', strategy: 'BREAKOUT' }, // PRODUCTION - REAL MONEY!
  { name: 'TOPORB', type: 'TOPORB', strategy: 'BREAKOUT' }, // TIER2
  { name: 'TIER3', type: 'TIER3', strategy: 'BREAKOUT' }, // TIER3
  { name: 'FUTURES', type: 'FUTURES', strategy: 'FUTURES' }, // Futures trading
  { name: 'V1', type: 'V1', strategy: 'BREAKOUT' }, // OPTIONS
  { name: 'E_OPTIONS', type: 'E_OPTIONS', strategy: 'BREAKOUT' }, // E-OPTIONS
  { name: 'OPTIONS_PCS', type: 'OPTIONS_PCS', strategy: 'PCS' },
  { name: 'GROK', type: 'GROK', strategy: 'BREAKOUT' },
];

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { hasInstanceAccess, token } = useAuth();
  
  // Filter instances based on user permissions
  const visibleInstances = instances.filter(instance => 
    hasInstanceAccess(instance.name)
  );
  
  // Instance data state
  const [instanceData, setInstanceData] = useState<{
    [key: string]: {
      positions: any[];
      accountSummary: any;
      loading: boolean;
      error: string | null;
    };
  }>({});

  // TIER2 sub-accounts state (Ilan, Jon, Avi)
  const [tier2SubAccounts, setTier2SubAccounts] = useState<{
    name: string;
    daily_pl: number;
    daily_pl_percent: number;
    equity?: number;
    error?: string;
  }[]>([]);

  // TIER3 sub-accounts state (Ilan, Jon, Avi)
  const [tier3SubAccounts, setTier3SubAccounts] = useState<{
    name: string;
    daily_pl: number;
    daily_pl_percent: number;
    equity?: number;
    error?: string;
  }[]>([]);

  // V1/OPTIONS sub-accounts state (Ilan, OPT_DAY_1)
  const [v1SubAccounts, setV1SubAccounts] = useState<{
    name: string;
    daily_pl: number;
    daily_pl_percent: number;
    equity?: number;
    error?: string;
  }[]>([]);

  // E_OPTIONS sub-accounts state (Ilan, Jon, Avi)
  const [eOptionsSubAccounts, setEOptionsSubAccounts] = useState<{
    name: string;
    daily_pl: number;
    daily_pl_percent: number;
    equity?: number;
    error?: string;
  }[]>([]);

  // LIVE sub-accounts state (Ilan, Jon, Avi)
  const [liveSubAccounts, setLiveSubAccounts] = useState<{
    name: string;
    daily_pl: number;
    daily_pl_percent: number;
    equity?: number;
    error?: string;
  }[]>([]);

  // Wheel/spread card state (grouped units per DASHBOARD_CARD_PROMPT.md), keyed by instance
  const [wheelCards, setWheelCards] = useState<{
    [instanceName: string]: {
      rows: {
        underlying: string;
        state: string;
        net_pl: number;
        spot?: number | null;
        basis?: number;
        strike?: number;
        long_strike?: number;
        expiry?: string;
        dte?: number;
        credit_total?: number;
        max_loss_total?: number;
        called_away?: number;
        otm_pct?: number;
        itm?: boolean;
        alert?: string | null;
        contracts?: number;
      }[];
      alerts: string[];
      income: {
        booked_today: number;
        premium_today: number;
        realized_today: number;
        mark_move_today: number;
      };
      context: {
        eod_date?: string;
        week_to_date?: { premium_collected?: number; realized_pnl?: number };
        all_time?: { total_pnl?: number; win_rate?: number; wins?: number; losses?: number };
      } | null;
    };
  }>({});

  // OPT-SPY-DAILY card state for V1 (one trade per day: open/closed/stand-aside)
  const [spyDailyCard, setSpyDailyCard] = useState<{
    status: string;
    trade: {
      direction?: string;
      short_strike?: number;
      long_strike?: number;
      contracts?: number;
      // v0.27.0: contracts < contracts_requested = partial entry fill
      contracts_requested?: number | null;
      fill_reason?: string | null;
      entry_credit_per_share?: number;
      entry_credit_total?: number;
      exit_level?: number;
      exit_level_kind?: string;
      exit_reason?: string | null;
      realized_pnl_total?: number | null;
      pct_of_credit_captured?: number | null;
    } | null;
    checkin: {
      mid?: number;
      captured?: number;
      upl?: number;
      spot?: number;
    } | null;
    stand_aside_reason: string | null;
    open_failed: { count: number; last_reason: string | null } | null;
    // v0.19.0: morning-event entry blackout (deliberate stand-aside)
    event_blackout?: { count: number; reason: string | null; event: string | null } | null;
    // v0.20.0: IV-crush detector lifted the blackout early (entry half-sized)
    adaptive_resume?: { iv_peak: number | null; iv_now: number | null } | null;
    realized_today: number | null;
    alert: string | null;
    gex: { regime?: string; atm_iv?: number | null; gap_pct?: number | null; gap_vs_sigma?: number | null; event_today?: string | null } | null;
    record: {
      realized_pnl_all_time?: number;
      closed_all_time?: number;
      wins_all_time?: number;
      losses_all_time?: number;
    } | null;
  } | null>(null);

  // Fetch data for a specific instance
  const fetchInstanceData = async (instanceName: string) => {
    // Only show loading state on initial load (no existing data)
    // On refresh, keep showing existing data (stale-while-revalidate pattern)
    const hasExistingData = instanceData[instanceName]?.accountSummary != null;
    
    if (!hasExistingData) {
      setInstanceData(prev => ({
        ...prev,
        [instanceName]: { ...prev[instanceName], loading: true, error: null }
      }));
    }

    try {
      // Get auth headers
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch positions and account summary in parallel
      const [positionsResponse, accountResponse] = await Promise.all([
        fetch(`/api/instances/${instanceName}/positions`, { headers }),
        fetch(`/api/instances/${instanceName}/account-summary`, { headers })
      ]);

      const positionsData = await positionsResponse.json();
      const accountData = await accountResponse.json();

      setInstanceData(prev => ({
        ...prev,
        [instanceName]: {
          positions: positionsData.positions || [],
          accountSummary: accountData.account_summary || null,
          loading: false,
          error: null
        }
      }));
    } catch (error) {
      setInstanceData(prev => ({
        ...prev,
        [instanceName]: {
          positions: [],
          accountSummary: null,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch data'
        }
      }));
    }
  };

  // Fetch data for all instances
  const fetchAllInstanceData = () => {
    visibleInstances.forEach(instance => {
      fetchInstanceData(instance.name);
    });
  };

  // Fetch TIER2 sub-accounts (Ilan, Jon, Avi)
  const fetchTier2SubAccounts = async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('/api/instances/TOPORB/sub-accounts', { headers });
      const data = await response.json();
      if (data.sub_accounts) {
        setTier2SubAccounts(data.sub_accounts);
      }
    } catch (error) {
      console.error('Error fetching TIER2 sub-accounts:', error);
    }
  };

  // Fetch TIER3 sub-accounts (Ilan, Jon, Avi)
  const fetchTier3SubAccounts = async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('/api/instances/TIER3/sub-accounts', { headers });
      const data = await response.json();
      if (data.sub_accounts) {
        setTier3SubAccounts(data.sub_accounts);
      }
    } catch (error) {
      console.error('Error fetching TIER3 sub-accounts:', error);
    }
  };

  // Fetch V1/OPTIONS sub-accounts (Ilan, Jon, Avi)
  const fetchV1SubAccounts = async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('/api/instances/V1/sub-accounts', { headers });
      const data = await response.json();
      if (data.sub_accounts) {
        setV1SubAccounts(data.sub_accounts);
      }
    } catch (error) {
      console.error('Error fetching V1 sub-accounts:', error);
    }
  };

  // Fetch E_OPTIONS sub-accounts (Ilan, Jon, Avi)
  const fetchEOptionsSubAccounts = async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('/api/instances/E_OPTIONS/sub-accounts', { headers });
      const data = await response.json();
      if (data.sub_accounts) {
        setEOptionsSubAccounts(data.sub_accounts);
      }
    } catch (error) {
      console.error('Error fetching E_OPTIONS sub-accounts:', error);
    }
  };

  // Fetch LIVE sub-accounts (Ilan, Jon, Avi)
  const fetchLiveSubAccounts = async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('/api/instances/LIVE/sub-accounts', { headers });
      const data = await response.json();
      if (data.sub_accounts) {
        setLiveSubAccounts(data.sub_accounts);
      }
    } catch (error) {
      console.error('Error fetching LIVE sub-accounts:', error);
    }
  };

  // Fetch wheel/spread cards (grouped units) for GROK and OPTIONS_PCS
  const fetchWheelCards = async () => {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    await Promise.all(WHEEL_CARD_INSTANCES.map(async (name) => {
      try {
        const response = await fetch(`/api/instances/${name}/wheel-positions`, { headers });
        const data = await response.json();
        if (data.rows) {
          setWheelCards(prev => ({ ...prev, [name]: data }));
        }
      } catch (error) {
        console.error(`Error fetching ${name} wheel positions:`, error);
      }
    }));
  };

  // Fetch the OPT-SPY-DAILY summary for the V1 card
  const fetchSpyDailyCard = async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('/api/instances/V1/spy-daily-card', { headers });
      const data = await response.json();
      if (data.status) {
        setSpyDailyCard(data);
      }
    } catch (error) {
      console.error('Error fetching V1 SPY daily card:', error);
    }
  };

  // Load data on component mount and set up polling
  useEffect(() => {
    fetchAllInstanceData();
    fetchTier2SubAccounts();
    fetchTier3SubAccounts();
    fetchV1SubAccounts();
    fetchEOptionsSubAccounts();
    fetchLiveSubAccounts();
    fetchWheelCards();
    fetchSpyDailyCard();
    
    // Poll every 60 seconds for updates - good balance between real-time and performance
    const interval = setInterval(() => {
      fetchAllInstanceData();
      fetchTier2SubAccounts();
      fetchTier3SubAccounts();
      fetchV1SubAccounts();
      fetchEOptionsSubAccounts();
      fetchLiveSubAccounts();
      fetchWheelCards();
      fetchSpyDailyCard();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // Listen for data import success to refresh data
  useEffect(() => {
    const handleDataImportSuccess = () => {
      setTimeout(fetchAllInstanceData, 2000); // Refresh after import completes
    };

    window.addEventListener('dataImportSuccess', handleDataImportSuccess);
    return () => window.removeEventListener('dataImportSuccess', handleDataImportSuccess);
  }, []);

  // Manual import function removed - no longer needed

  return (
    <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
      <Box sx={{ 
        mb: 4,
        p: { xs: 2, sm: 3 },
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        boxShadow: 3
      }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
          Trading Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          Monitor and manage your trading instances
        </Typography>
      </Box>
      
      {/* Instance Cards */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Trading Instances
      </Typography>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {visibleInstances.map((instance) => {
          const data = instanceData[instance.name];
          const positions = data?.positions || [];
          const accountSummary = data?.accountSummary;
          const isLoading = data?.loading;
          const error = data?.error;
          // Grouped wheel/spread card data (GROK, OPTIONS_PCS)
          const wheelCard = WHEEL_CARD_INSTANCES.includes(instance.name) ? wheelCards[instance.name] : undefined;
          // OPT-SPY-DAILY day summary (V1 only)
          const spyCard = instance.name === 'V1' ? spyDailyCard : null;
          const spyTrade = spyCard?.trade || null;
          const spyExit = spyTrade?.exit_reason
            ? (SPY_EXIT_REASONS[spyTrade.exit_reason] || { label: spyTrade.exit_reason.replace(/_/g, ' ').toUpperCase(), color: 'default' as const })
            : null;
          // Map internal instance names to display names
          const displayNameMap: { [key: string]: string } = {
            'V1': 'OPTIONS',
            'E_OPTIONS': 'E-OPTIONS',
            'OPTIONS_PCS': 'OPTIONS PCS',
            'TOPORB': 'TIER2',
            'TIER3': 'TIER3',
            'LIVE': 'LIVE',
            'FUTURES': 'Futures'
          };
          const displayName = displayNameMap[instance.name] || instance.name;
          return (
            <Grid item xs={12} sm={6} md={6} lg={3} key={instance.name}>
              <Card sx={{
                boxShadow: 2,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}>
                <CardActionArea onClick={() => navigate(`/instance/${instance.name}`)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <AccountTreeIcon
                        sx={{
                          fontSize: 40,
                          color:
                            (instance.name === 'V1' || instance.name === 'E_OPTIONS' || instance.name === 'OPTIONS_PCS' || instance.name === 'GROK')
                              ? '#9c27b0'
                              : instance.name === 'FUTURES'
                                ? '#f9a825'
                                : (instance.name === 'LIVE' || instance.name === 'TOPORB' || instance.name === 'TIER3')
                                  ? 'success.main'
                                  : undefined,
                        }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <Chip 
                          label={
                            instance.name === 'V1'
                              ? 'OPTIONS DAILY SPY'
                              : instance.name === 'E_OPTIONS'
                                ? 'OPTIONS CSP'
                                : instance.name === 'GROK'
                                  ? 'OPTIONS CSP MAG7'
                                  : instance.name === 'OPTIONS_PCS'
                                    ? 'OPTIONS PCS'
                                    : instance.name === 'FUTURES'
                                      ? 'FUTURES'
                                      : instance.strategy
                          } 
                          size="small"
                          variant="outlined"
                          sx={{
                            ...(instance.strategy === 'LONG' && {
                              color: 'success.main',
                              borderColor: 'success.main',
                            }),
                            ...(instance.strategy === 'SHORT' && {
                              color: 'error.main',
                              borderColor: 'error.main',
                            }),
                            ...(instance.name === 'FUTURES' && {
                              color: '#f9a825',
                              borderColor: '#f9a825',
                            }),
                            ...(instance.name === 'OPTIONS_PCS' && {
                              color: '#9c27b0',
                              borderColor: '#9c27b0',
                            }),
                            ...((instance.strategy === 'BREAKOUT' || instance.strategy === 'HYBRID BREAKOUT') && (
                              (instance.name === 'V1' || instance.name === 'E_OPTIONS' || instance.name === 'GROK')
                                  ? { color: '#9c27b0', borderColor: '#9c27b0' }
                                  : { color: 'success.main', borderColor: 'success.main' }
                            )),
                          }}
                        />
                        {(instance.name === 'V1' || instance.name === 'FUTURES' || instance.name === 'GROK') ? (
                          <Chip
                            label="LIVE"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              color: '#fff',
                              bgcolor: '#d32f2f',
                              '& .MuiChip-label': { px: 0.8 },
                            }}
                          />
                        ) : (
                          <Chip
                            label="DEMO"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              color: '#fff',
                              bgcolor: '#1976d2',
                              '& .MuiChip-label': { px: 0.8 },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    
                    <Typography variant="h5" component="div" sx={{ mb: 1 }}>
                      {displayName}
                    </Typography>
                    
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      {displayName} Instance
                    </Typography>

                    {/* Loading State - only show if no data yet */}
                    {isLoading && !accountSummary && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Loading...
                        </Typography>
                      </Box>
                    )}

                    {/* Error State */}
                    {error && (
                      <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                        <Typography variant="caption">
                          {error}
                        </Typography>
                      </Alert>
                    )}

                    {/* Wheel/spread cards: income booked vs unrealized mark move (headline
                        split). v3 relabel (2026-09-04): "paper" is reserved for the retired
                        paper account and must not describe unrealized P&L on any panel. */}
                    {wheelCard && !error && !WHEEL_CARD_KEEP_PNL.includes(instance.name) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Income booked today
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: wheelCard.income.booked_today >= 0 ? 'success.main' : 'error.main',
                              fontWeight: 'bold'
                            }}
                          >
                            {fmtSigned(wheelCard.income.booked_today)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            mark move (unrealized): {fmtSigned(wheelCard.income.mark_move_today)}
                          </Typography>
                        </Box>
                        {wheelCard.context && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {wheelCard.context.week_to_date && (
                              <>
                                WTD income {fmtSigned(
                                  (wheelCard.context.week_to_date.premium_collected || 0) +
                                  (wheelCard.context.week_to_date.realized_pnl || 0)
                                )}
                                {' · '}
                              </>
                            )}
                            All-time {fmtSigned(wheelCard.context.all_time?.total_pnl)}
                            {wheelCard.context.all_time?.win_rate != null &&
                              ` (${(wheelCard.context.all_time.win_rate * 100).toFixed(0)}% wins)`}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Today's P&L - show even during refresh if we have data */}
                    {accountSummary && !error && (!wheelCard || WHEEL_CARD_KEEP_PNL.includes(instance.name)) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Today's P&L
                        </Typography>
                        
                        {/* LIVE, TIER2, TIER3, V1/OPTIONS, E_OPTIONS: Show all sub-accounts (Ilan, Jon, Avi) */}
                        {((instance.name === 'LIVE' && liveSubAccounts.length > 0) ||
                          (instance.name === 'TOPORB' && tier2SubAccounts.length > 0) ||
                          (instance.name === 'TIER3' && tier3SubAccounts.length > 0) || 
                          (instance.name === 'V1' && v1SubAccounts.length > 0) ||
                          (instance.name === 'E_OPTIONS' && eOptionsSubAccounts.length > 0)) ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {(instance.name === 'LIVE' ? liveSubAccounts : (instance.name === 'TOPORB' ? tier2SubAccounts : (instance.name === 'TIER3' ? tier3SubAccounts : (instance.name === 'E_OPTIONS' ? eOptionsSubAccounts : v1SubAccounts)))).map((subAccount) => (
                              subAccount.error ? (
                                // Dead credential / fetch failure: never disguise it as +$0.00
                                <Box key={subAccount.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Tooltip title={`Data unavailable: ${subAccount.error}`} arrow>
                                    <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 14 }} />
                                  </Tooltip>
                                  <Typography
                                    variant="body1"
                                    sx={{ color: 'text.disabled', fontWeight: 'bold', fontSize: '0.95rem' }}
                                  >
                                    —
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'warning.main', fontSize: '0.7rem' }}>
                                    feed error
                                  </Typography>
                                  <Typography
                                    sx={{ ml: 'auto', fontSize: '0.85rem', fontWeight: 'bold', color: 'text.secondary' }}
                                  >
                                    {subAccount.name}
                                  </Typography>
                                </Box>
                              ) : (
                              <Box key={subAccount.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {subAccount.daily_pl >= 0 ? (
                                  <TrendingUpIcon sx={{ color: 'success.main', fontSize: 14 }} />
                                ) : (
                                  <TrendingDownIcon sx={{ color: 'error.main', fontSize: 14 }} />
                                )}
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    color: subAccount.daily_pl >= 0 ? 'success.main' : 'error.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.95rem'
                                  }}
                                >
                                  {subAccount.daily_pl >= 0 ? '+' : ''}${subAccount.daily_pl.toFixed(2)}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: subAccount.daily_pl >= 0 ? 'success.main' : 'error.main',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  ({subAccount.daily_pl_percent >= 0 ? '+' : ''}{subAccount.daily_pl_percent.toFixed(2)}%)
                                </Typography>
                                <Typography 
                                  sx={{ 
                                    ml: 'auto',
                                    fontSize: '0.85rem',
                                    fontWeight: 'bold',
                                    color: 'text.secondary'
                                  }}
                                >
                                  {subAccount.name}
                                </Typography>
                              </Box>
                              )
                            ))}
                          </Box>
                        ) : (
                          /* Standard single account display */
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {accountSummary.daily_pl >= 0 ? (
                              <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
                            ) : (
                              <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
                            )}
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                color: accountSummary.daily_pl >= 0 ? 'success.main' : 'error.main',
                                fontWeight: 'bold'
                              }}
                            >
                              {accountSummary.daily_pl >= 0 ? '+' : ''}${accountSummary.daily_pl.toFixed(2)}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: accountSummary.daily_pl >= 0 ? 'success.main' : 'error.main'
                              }}
                            >
                              ({accountSummary.daily_pl_percent >= 0 ? '+' : ''}{accountSummary.daily_pl_percent.toFixed(2)}%)
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Wheel/spread cards: grouped units (one row per underlying, never per leg) */}
                    {!error && wheelCard && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {instance.name === 'OPTIONS_PCS' ? 'Open Spreads' : 'Wheel Positions'} ({wheelCard.rows.length})
                        </Typography>

                        {/* Compact income line for cards that keep the sub-accounts P&L block */}
                        {WHEEL_CARD_KEEP_PNL.includes(instance.name) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Income booked today{' '}
                            <span style={{
                              color: wheelCard.income.booked_today >= 0 ? '#66bb6a' : '#f44336',
                              fontWeight: 'bold'
                            }}>
                              {fmtSigned(wheelCard.income.booked_today)}
                            </span>
                            {' · '}mark move (unrealized) {fmtSigned(wheelCard.income.mark_move_today)}
                            {wheelCard.context?.all_time?.total_pnl != null && (
                              <>
                                {' · '}All-time {fmtSigned(wheelCard.context.all_time.total_pnl)}
                                {wheelCard.context.all_time.win_rate != null &&
                                  ` (${(wheelCard.context.all_time.win_rate * 100).toFixed(0)}% wins)`}
                              </>
                            )}
                          </Typography>
                        )}

                        {wheelCard.alerts.length > 0 && (
                          <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                            {wheelCard.alerts.map((a) => (
                              <Typography key={a} variant="caption" display="block">
                                ⚠ {a}
                              </Typography>
                            ))}
                          </Alert>
                        )}

                        {wheelCard.rows.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No open positions
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {wheelCard.rows.map((row) => (
                              <Box key={row.underlying}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={row.underlying}
                                    size="small"
                                    variant="outlined"
                                    sx={{ minWidth: 56, fontSize: '0.7rem' }}
                                  />
                                  <Tooltip title={wheelStateTooltip(row.state)} arrow>
                                    <Chip
                                      label={row.state}
                                      size="small"
                                      color={wheelStateChipColor(row.state)}
                                      sx={{ fontSize: '0.6rem', minWidth: 60 }}
                                    />
                                  </Tooltip>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: row.net_pl >= 0 ? 'success.main' : 'error.main',
                                      fontWeight: 'bold',
                                      fontSize: '0.75rem',
                                      ml: 'auto'
                                    }}
                                  >
                                    {fmtSigned(row.net_pl)}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', pl: 0.5, fontSize: '0.65rem', lineHeight: 1.4 }}
                                >
                                  {row.state === 'COVERED' && (
                                    <>
                                      basis ${row.basis?.toFixed(2) ?? '—'} → spot ${row.spot?.toFixed(2) ?? '—'}
                                      {row.strike != null && ` · ${row.strike}C ${fmtExpiry(row.expiry)}`}
                                      {row.called_away != null && (
                                        <>
                                          {' · called away = '}
                                          <span style={{
                                            color: row.called_away >= 0 ? '#66bb6a' : '#f44336',
                                            fontWeight: 'bold'
                                          }}>
                                            {fmtSigned(row.called_away)}
                                          </span>
                                        </>
                                      )}
                                    </>
                                  )}
                                  {row.state === 'CSP' && (
                                    <>
                                      ${row.strike}P {fmtExpiry(row.expiry)}
                                      {row.otm_pct != null &&
                                        ` · ${row.itm ? `${Math.abs(row.otm_pct).toFixed(1)}% ITM` : `${row.otm_pct.toFixed(1)}% OTM`}`}
                                      {row.credit_total != null && ` · credit $${Math.round(row.credit_total)}`}
                                    </>
                                  )}
                                  {row.state === 'PCS' && (
                                    <>
                                      {row.strike}/{row.long_strike}P {fmtExpiry(row.expiry)}
                                      {row.otm_pct != null &&
                                        ` · ${row.itm ? `${Math.abs(row.otm_pct).toFixed(1)}% ITM` : `${row.otm_pct.toFixed(1)}% OTM`}`}
                                      {row.credit_total != null && ` · credit $${Math.round(row.credit_total)}`}
                                      {row.max_loss_total != null && ` · max loss $${Math.round(row.max_loss_total)}`}
                                    </>
                                  )}
                                  {row.state === 'SHARES' && (
                                    <>
                                      basis ${row.basis?.toFixed(2) ?? '—'} → spot ${row.spot?.toFixed(2) ?? '—'} · no call sold yet
                                    </>
                                  )}
                                  {row.state === 'OTHER' && <>mixed legs — see instance view</>}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* OPT-SPY-DAILY card (V1): the day's single trade — open / closed / stand-aside */}
                    {!error && spyCard && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="text.secondary">
                            SPY Daily
                          </Typography>
                          {spyCard.gex?.regime && (
                            <Tooltip
                              title={
                                spyCard.gex.regime === 'positive'
                                  ? 'Dealers dampen moves; good day to sell premium'
                                  : spyCard.gex.regime === 'negative'
                                    ? 'Dealers amplify moves; only half-sized wall plays trade (put wall if IV is calm, call-wall fade otherwise)'
                                    : 'Unclear tape; half size'
                              }
                              arrow
                            >
                              <Chip
                                label={spyCard.gex.regime.toUpperCase()}
                                size="small"
                                color={
                                  spyCard.gex.regime === 'positive'
                                    ? 'success'
                                    : spyCard.gex.regime === 'negative'
                                      ? 'error'
                                      : 'default'
                                }
                                sx={{ fontSize: '0.6rem', height: 18 }}
                              />
                            </Tooltip>
                          )}
                          {spyCard.gex?.atm_iv != null && (
                            <Chip
                              label={`0DTE IV ${(spyCard.gex.atm_iv * 100).toFixed(1)}%`}
                              size="small"
                              variant="outlined"
                              color={spyIvColor(spyCard.gex.atm_iv)}
                              sx={{ fontSize: '0.6rem', height: 18 }}
                            />
                          )}
                          {spyCard.gex?.gap_pct != null && (
                            <Chip
                              label={`gap ${spyCard.gex.gap_pct >= 0 ? '+' : ''}${(spyCard.gex.gap_pct * 100).toFixed(1)}%${spyCard.gex.gap_vs_sigma != null ? ` (${spyCard.gex.gap_vs_sigma >= 0 ? '+' : ''}${spyCard.gex.gap_vs_sigma.toFixed(1)}σ)` : ''}`}
                              size="small"
                              variant="outlined"
                              color={spyCard.gex.gap_vs_sigma != null && Math.abs(spyCard.gex.gap_vs_sigma) >= 1 ? 'warning' : 'default'}
                              sx={{ fontSize: '0.6rem', height: 18 }}
                            />
                          )}
                          {spyCard.gex?.event_today && (
                            <Tooltip
                              title={spyShortEvent(spyCard.gex.event_today) === 'FOMC'
                                ? `${spyCard.gex.event_today} — everything is force-closed by 13:30 ET`
                                : spyCard.gex.event_today}
                              arrow
                            >
                              <Chip
                                label={`${spyShortEvent(spyCard.gex.event_today)} day`}
                                size="small"
                                variant="outlined"
                                color={spyShortEvent(spyCard.gex.event_today) === 'FOMC' ? 'warning' : 'default'}
                                sx={{ fontSize: '0.6rem', height: 18 }}
                              />
                            </Tooltip>
                          )}
                        </Box>

                        {spyCard.alert && (
                          <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                            <Typography variant="caption">⚠ {spyCard.alert}</Typography>
                          </Alert>
                        )}

                        {(spyCard.status === 'open' || spyCard.status === 'closed') && spyTrade ? (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {/* v0.27.0: a partial entry reads "× 1 of 30", never as a 1-lot trade */}
                              <Chip
                                label={`SPY ${fmtStrike(spyTrade.short_strike)}/${fmtStrike(spyTrade.long_strike)}${spyTrade.direction === 'call_credit' ? 'C' : 'P'} × ${
                                  spyTrade.contracts_requested != null && spyTrade.contracts != null && spyTrade.contracts < spyTrade.contracts_requested
                                    ? `${spyTrade.contracts} of ${spyTrade.contracts_requested}`
                                    : spyTrade.contracts ?? '—'
                                }`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              {spyTrade.contracts_requested != null && spyTrade.contracts != null && spyTrade.contracts < spyTrade.contracts_requested && (
                                <Chip label="PARTIAL FILL" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.6rem' }} />
                              )}
                              {spyCard.status === 'open' ? (
                                <Chip label="OPEN" size="small" color="info" sx={{ fontSize: '0.6rem' }} />
                              ) : (
                                spyExit && (
                                  <Chip label={spyExit.label} size="small" color={spyExit.color} sx={{ fontSize: '0.6rem' }} />
                                )
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    (spyCard.status === 'open' ? (spyCard.checkin?.upl ?? 0) : (spyTrade.realized_pnl_total ?? 0)) >= 0
                                      ? 'success.main'
                                      : 'error.main',
                                  fontWeight: 'bold',
                                  fontSize: '0.75rem',
                                  ml: 'auto',
                                }}
                              >
                                {spyCard.status === 'open'
                                  ? fmtSigned(spyCard.checkin?.upl)
                                  : fmtSigned(spyTrade.realized_pnl_total)}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', pl: 0.5, fontSize: '0.65rem', lineHeight: 1.4 }}
                            >
                              {spyTrade.entry_credit_per_share != null && `credit $${spyTrade.entry_credit_per_share.toFixed(2)}`}
                              {spyCard.status === 'open' ? (
                                <>
                                  {spyCard.checkin?.captured != null && ` · ${spyCard.checkin.captured}% captured`}
                                  {spyTrade.exit_level != null &&
                                    ` · auto-exit ${fmtStrike(spyTrade.exit_level)}${spyTrade.exit_level_kind ? ` (${spyTrade.exit_level_kind.replace(/_/g, ' ')})` : ''}`}
                                  {spyCard.checkin?.spot != null && ` · spot ${spyCard.checkin.spot.toFixed(2)}`}
                                </>
                              ) : (
                                <>
                                  {spyTrade.pct_of_credit_captured != null &&
                                    ` · ${(spyTrade.pct_of_credit_captured * 100).toFixed(0)}% of credit captured`}
                                </>
                              )}
                            </Typography>
                            {/* v0.20.0: entry after an early adaptive resume is deliberately half-sized */}
                            {spyCard.adaptive_resume && (
                              <Typography variant="caption" sx={{ display: 'block', pl: 0.5, fontSize: '0.65rem', color: '#81c784' }}>
                                entered early at half size after the post-event IV crush
                                {spyCard.adaptive_resume.iv_peak != null && spyCard.adaptive_resume.iv_now != null
                                  ? ` (IV ${(spyCard.adaptive_resume.iv_peak * 100).toFixed(1)}% → ${(spyCard.adaptive_resume.iv_now * 100).toFixed(1)}%)`
                                  : ''}
                              </Typography>
                            )}
                          </Box>
                        ) : spyCard.status === 'no_fill' ? (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No fill today — tried to enter {spyCard.open_failed?.count ? `${spyCard.open_failed.count}× ` : ''}
                              but {prettySpyOpenFailed(spyCard.open_failed?.last_reason)}
                            </Typography>
                            {spyCard.stand_aside_reason && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 0.5, fontSize: '0.65rem' }}>
                                latest gate: {prettySpyStandAside(spyCard.stand_aside_reason)}
                              </Typography>
                            )}
                          </Box>
                        ) : spyCard.status === 'stand_aside' ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No trade today — {prettySpyStandAside(spyCard.stand_aside_reason || '')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No activity yet today
                          </Typography>
                        )}

                        {spyCard.record && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            Realized today{' '}
                            <span
                              style={{
                                color: (spyCard.realized_today ?? 0) >= 0 ? '#66bb6a' : '#f44336',
                                fontWeight: 'bold',
                              }}
                            >
                              {fmtSigned(spyCard.realized_today)}
                            </span>
                            {' · '}All-time {fmtSigned(spyCard.record.realized_pnl_all_time)}
                            {spyCard.record.closed_all_time
                              ? ` (${spyCard.record.wins_all_time ?? 0}W/${spyCard.record.losses_all_time ?? 0}L)`
                              : ' (no closed trades yet — live era)'}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* FUTURES: bot-written status strip (entries window, setup, stop stage / R) */}
                    {instance.name === 'FUTURES' && <FuturesStatusPanel compact pollMs={30000} />}

                    {/* Current Positions - show even during refresh */}
                    {!error && !wheelCard && !spyCard && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Open Positions ({positions.length})
                        </Typography>
                        
                        {positions.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No open positions
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {positions.map((position, index) => (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                  label={position.symbol} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ minWidth: 50, fontSize: '0.7rem' }}
                                />
                                <Chip 
                                  label={getPositionSideLabel(position.symbol, position.side)} 
                                  size="small"
                                  color={position.side === 'long' ? 'success' : 'error'}
                                  sx={{ fontSize: '0.6rem', minWidth: 40 }}
                                />
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: position.unrealized_pl >= 0 ? 'success.main' : 'error.main',
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem'
                                  }}
                                >
                                  {position.unrealized_pl >= 0 ? '+' : ''}${position.unrealized_pl.toFixed(2)}
                                  {' '}
                                  <span style={{ opacity: 0.8 }}>
                                    ({position.unrealized_plpc >= 0 ? '+' : ''}{(position.unrealized_plpc * 100).toFixed(2)}%)
                                  </span>
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        }        )}
      </Grid>
    </Box>
  );
};

export default Dashboard; 